import React, { useEffect, useState } from "react";
import { FileText, Download, Eye, ChevronDown, Calendar, Hash } from "lucide-react";
import api from "../lib/api";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const pdfUrl = (id) => `${API_BASE}/api/coas/${id}/pdf`;

const formatDate = (iso) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
};

const sizeLabel = (b) => {
    if (!b) return "";
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

/**
 * COA section shown on every product page.
 * Automatically displays the current (latest) assigned COA + an expandable list
 * of previous versions if more than one exists. Renders nothing if no COA.
 */
export const ProductCoaSection = ({ slug }) => {
    const [data, setData] = useState(null);
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await api.get(`/products/${slug}/coa`);
                if (!cancelled) setData(r.data);
            } catch { /* ignore */ }
        })();
        return () => { cancelled = true; };
    }, [slug]);

    if (!data || !data.current) return null;
    const cur = data.current;
    const olderVersions = (data.versions || []).filter((v) => v.id !== cur.id);

    return (
        <section
            className="mt-10 rounded-2xl border border-emerald-500/25 bg-gradient-to-br from-emerald-500/[0.05] to-transparent p-6 md:p-8"
            data-testid="product-coa-section"
        >
            <div className="flex items-start gap-4 flex-wrap">
                <div className="w-12 h-12 shrink-0 rounded-full bg-emerald-500/15 flex items-center justify-center">
                    <FileText size={20} className="text-emerald-300" />
                </div>
                <div className="flex-1 min-w-[220px]">
                    <h2 className="font-display text-xl md:text-2xl font-bold text-white tracking-tight">
                        Certificate of Analysis
                    </h2>
                    <p className="text-xs text-[var(--text-muted)] font-mono uppercase tracking-widest mt-0.5">
                        Manufacturer-supplied · current batch
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <a
                        href={pdfUrl(cur.id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border border-[var(--gold)]/40 text-[var(--gold)] hover:bg-[var(--gold)]/10 text-xs font-mono uppercase tracking-widest"
                        data-testid={`coa-view-${cur.id}`}
                    >
                        <Eye size={12} /> View COA
                    </a>
                    <a
                        href={pdfUrl(cur.id)}
                        download={cur.filename}
                        className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-[var(--gold)] text-black text-xs font-mono uppercase tracking-widest font-bold hover:bg-[#F5C932]"
                        data-testid={`coa-download-${cur.id}`}
                    >
                        <Download size={12} /> Download
                    </a>
                </div>
            </div>

            <div className="grid sm:grid-cols-3 gap-3 mt-5">
                {cur.batch_number && (
                    <div className="px-3 py-2 rounded-xl bg-black/30 border border-white/5">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">Batch</div>
                        <div className="text-sm text-white font-mono flex items-center gap-1.5"><Hash size={11} className="text-emerald-300" />{cur.batch_number}</div>
                    </div>
                )}
                {cur.manufacturer && (
                    <div className="px-3 py-2 rounded-xl bg-black/30 border border-white/5">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">Manufacturer</div>
                        <div className="text-sm text-white truncate">{cur.manufacturer}</div>
                    </div>
                )}
                <div className="px-3 py-2 rounded-xl bg-black/30 border border-white/5">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">Uploaded</div>
                    <div className="text-sm text-white inline-flex items-center gap-1.5"><Calendar size={11} className="text-emerald-300" />{formatDate(cur.upload_date)}</div>
                </div>
            </div>

            {olderVersions.length > 0 && (
                <div className="mt-5">
                    <button
                        onClick={() => setExpanded((v) => !v)}
                        className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-[var(--text-secondary)] hover:text-[var(--gold)]"
                        data-testid="coa-previous-toggle"
                    >
                        <ChevronDown size={12} className={`transition-transform ${expanded ? "rotate-180" : ""}`} />
                        {olderVersions.length} previous version{olderVersions.length === 1 ? "" : "s"}
                    </button>
                    {expanded && (
                        <ul className="mt-3 space-y-2" data-testid="coa-previous-list">
                            {olderVersions.map((v) => (
                                <li
                                    key={v.id}
                                    className="flex items-center justify-between gap-3 px-3 py-2 rounded-xl bg-black/30 border border-white/5"
                                    data-testid={`coa-version-${v.id}`}
                                >
                                    <div className="text-xs text-white">
                                        <span className="font-mono text-[var(--gold)]">{v.batch_number || "Batch —"}</span>
                                        <span className="text-[var(--text-muted)] ml-2">· {formatDate(v.upload_date)} · {sizeLabel(v.file_size_bytes)}</span>
                                    </div>
                                    <div className="flex gap-2">
                                        <a href={pdfUrl(v.id)} target="_blank" rel="noopener noreferrer" className="text-[var(--gold)] hover:underline text-xs font-mono uppercase tracking-widest">View</a>
                                        <a href={pdfUrl(v.id)} download={v.filename} className="text-[var(--text-secondary)] hover:text-white text-xs font-mono uppercase tracking-widest">Download</a>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </section>
    );
};
